import React, { useMemo, useState } from "react";
import { Divider, Grid, lighten, Link, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { IRemyndScrapeCategoryMappingPreviewResp } from "../types";

const useStyles = makeStyles(theme => ({
  ul: {
    marginTop: theme.spacing(1),
    marginBottom: 0,
    "&> ul": {
      marginTop: 4,
      marginBottom: 4
    }
  },
  expander: {
    marginLeft: theme.spacing(1)
  }
}));

const MessageList = (props: {
  title: string;
  messages: string[];
  severity: "error" | "warning" | "info";
  children?: React.ReactNode;
}) => {
  const classes = useStyles();
  const { messages, severity, title, children } = props;

  const defaultExpanded = severity !== "warning";
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Alert severity={severity}>
      <Typography variant="subtitle2">
        <strong>{title}</strong>
        <Link
          href="#"
          onClick={() => setExpanded(!expanded)}
          className={classes.expander}
        >
          {expanded ? "Collapse" : "Expand"}
        </Link>
      </Typography>
      {expanded && (
        <ul className={classes.ul}>
          {children}
          {messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </Alert>
  );
};

// ---------------------------------------------------------------------------

const useTableStyles = makeStyles(theme => {
  const getBackgroundColor = (color: string) => lighten(color, 0.85);
  const getHoverBackgroundColor = (color: string) => lighten(color, 0.75);
  return {
    root: {
      "& .row-theme--INSERT": {
        backgroundColor: getBackgroundColor(theme.palette.success.main),
        "&:hover": {
          backgroundColor: getHoverBackgroundColor(theme.palette.success.main)
        }
      },
      "& .row-theme--UPDATE": {
        backgroundColor: getBackgroundColor(theme.palette.info.main),
        "&:hover": {
          backgroundColor: getHoverBackgroundColor(theme.palette.info.main)
        }
      },
      "& .row-theme--IGNORE": {
        backgroundColor: getBackgroundColor(theme.palette.grey.A400),
        "&:hover": {
          backgroundColor: getHoverBackgroundColor(theme.palette.grey.A400)
        }
      }
    }
  };
});

function idToString<T>(
  id: string | undefined,
  dataMap: Record<UUID, T>,
  dataToString: (data: T) => string
) {
  if (id) {
    const data = dataMap[id];
    return data ? dataToString(data) : "";
  } else {
    return "";
  }
}

const formatName = (d: { nameTh: string; nameEn: string }) =>
  `${d.nameTh} (${d.nameEn})`;
const formatCodeName = (d: { code: string; nameTh: string; nameEn: string }) =>
  `${d.code} : ${d.nameTh} (${d.nameEn})`;

const PreviewTable = (props: {
  data: IRemyndScrapeCategoryMappingPreviewResp;
}) => {
  const classes = useTableStyles();
  const { data } = props;

  const [pageSize, setPageSize] = useState(10);
  const itemsWithId = useMemo(
    () =>
      data.items.map((it, idx) => ({
        ...it,
        id: idx,
        remyndBrand: idToString(
          it.category.brandId,
          data.remyndBrandMap,
          formatName
        ),
        productCount: it.category.productCount,
        hasMappedCategory: it.category.hasMappedCategory,
        categoryTextTh: it.category.categoryTextTh,
        categoryTextEn: it.category.categoryTextEn,
        ruleOrd: it.matchedRule?.ord,
        remyndCategoryLv1: idToString(
          it.matchedRule?.categoryLv1Id,
          data.remyndCategoryLv1Map,
          formatCodeName
        ),
        remyndCategoryLv2: idToString(
          it.matchedRule?.categoryLv2Id,
          data.remyndCategoryLv2Map,
          formatCodeName
        ),
        remyndCategoryLv3: idToString(
          it.matchedRule?.categoryLv3Id,
          data.remyndCategoryLv3Map,
          formatCodeName
        )
      })),
    [data]
  );
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "remyndBrand",
        headerName: "แบรนด์",
        width: 180
      },
      {
        field: "categoryTextTh",
        headerName: "หมวดหมู่ (TH)",
        width: 400
      },
      {
        field: "categoryTextEn",
        headerName: "หมวดหมู่ (EN)",
        width: 400
      },
      {
        field: "productCount",
        headerName: "Products",
        width: 120,
        align: "right"
      },
      {
        field: "ruleOrd",
        headerName: "Rule#",
        width: 100,
        align: "right"
      },
      {
        field: "remyndCategoryLv1",
        headerName: "RemyndCatLv1",
        width: 200
      },
      {
        field: "remyndCategoryLv2",
        headerName: "RemyndCatLv2",
        width: 200
      },
      {
        field: "remyndCategoryLv3",
        headerName: "RemyndCatLv3",
        width: 200
      }
    ],
    []
  );

  return (
    <div className={classes.root}>
      <DataGrid
        rows={itemsWithId}
        columns={columns}
        density="compact"
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[10, 50, 100]}
        disableSelectionOnClick
        disableColumnMenu
        autoHeight
        components={{
          Toolbar: GridToolbar
        }}
        getRowClassName={params =>
          `row-theme--${
            params.getValue(params.id, "ruleOrd")
              ? !params.getValue(params.id, "hasMappedCategory")
                ? "INSERT"
                : "UPDATE"
              : "IGNORE"
          }`
        }
      />
    </div>
  );
};

// ---------------------------------------------------------------------------

type Props = {
  data: IRemyndScrapeCategoryMappingPreviewResp;
};

const PreviewUpdateForm: React.FC<Props> = props => {
  const classes = useStyles();
  const { warnings } = props.data;

  return (
    <>
      <Grid item xs={12}>
        <Divider light />
      </Grid>

      <Grid item xs={12}>
        <Alert severity="info">
          หมายเหตุ รายการในตาราง:
          <ul className={classes.ul}>
            <li>{`สีเขียว = สามารถ map ได้ และทุกสินค้ายังไม่มีการ map หมวดหมู่มาก่อน`}</li>
            <li>{`สีฟ้า = สามารถ map ได้ แต่บางสินค้ามี map หมวดหมู่ไว้อยู่แล้ว (อาจจะเหมือนหรือต่างจากของใหม่ก็ได้)`}</li>
            <li>{`สีเทา = ไม่พบ mapping rule ที่ตรง`}</li>
          </ul>
        </Alert>
      </Grid>

      {warnings.length > 0 && (
        <Grid item xs={12}>
          <MessageList
            title={`Warnings (${warnings.length})`}
            messages={warnings}
            severity="warning"
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <PreviewTable data={props.data} />
      </Grid>
    </>
  );
};

export default PreviewUpdateForm;
